// src/components/Questionnaires/GoalsSettingPartA.js

import React, {useEffect, useState} from 'react';
import {Card, Form, Input, Button, Alert, Typography, message, Spin} from 'antd';
import moment from 'moment';
import "./GoalsSettingPartA.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const GoalsSettingPartA = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/goals-setting-partA/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/goals-setting-partA/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${server_url}/questionnaires/goals-setting-partA/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: values,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit Goals Setting Part A.');
            }

            const data = await response.json();
            message.success('Goals Setting Part A submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Goals Setting Part A:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>

            <div className="container">
                <h2 className="header">PART A (2) - Self-Evaluation</h2>

                <table className="evaluation-table">
                    <tr>
                        <th colSpan="2" className="table-header">Circle the most appropriate option.</th>
                    </tr>
                    <tr>
                        <td className="table-cell">
                            Last year, my performance at school was...
                            <div className="range-container">
                                <span>&#x2b05; Poor</span>
                                <input type="range" min="1" max="10" step="1" className="range-input"/>
                                <span>Excellent &#x27a1;</span>
                            </div>
                        </td>
                        <td className="table-cell">
                            My teachers thought my efforts last year were...
                            <div className="range-container">
                                <span>&#x2b05; Poor</span>
                                <input type="range" min="1" max="10" step="1" className="range-input"/>
                                <span>Excellent &#x27a1;</span>
                            </div>
                        </td>
                    </tr>
                </table>

                <label>This year, I will try new things like...</label>
                <input type="text" className="text-input"/>

                <br/><br/>
                <label>I will work harder on things like...</label>
                <input type="text" className="text-input"/>

                <br/><br/>
                <div className="goals-banner">Goals!</div>

                <label>My goal in school last year was...</label>
                <input type="text" className="text-input"/>

                <br/><br/>
                <label>Why was the goal important to you?</label>
                <textarea className="textarea-extra"></textarea>

                <br/><br/>
                <label>What’s the status of the goal?</label>
                <div className="status-container">
                    <input type="checkbox"/> Accomplished
                    <input type="checkbox"/> Still working
                </div>

                <br/>
                <label>ONE specific goal I would like to achieve in school by the end of this year:</label>
                <input type="text" className="text-input"/>

                <br/><br/>
                <label>How will I know I have achieved this goal?</label>
                <input type="text" className="text-input"/>

                <br/><br/>
                <label>To achieve this goal, things I need to do:</label>
                <table className="action-table">
                    <tr>
                        <th>A week from now:</th>
                        <th>6 weeks from now:</th>
                        <th>6 months from now:</th>
                    </tr>
                    <tr>
                        <td><textarea className="textarea-basic"></textarea></td>
                        <td><textarea className="textarea-basic"></textarea></td>
                        <td><textarea className="textarea-basic"></textarea></td>
                    </tr>
                </table>

                <label>Things that will help me achieve this goal:</label>
                <table className="support-table">
                    <tr>
                        <th>My strengths/abilities</th>
                        <th>Support I get at home/in the community</th>
                        <th>Support I get at school</th>
                    </tr>
                    <tr>
                        <td><textarea className="textarea-basic"></textarea></td>
                        <td><textarea className="textarea-basic"></textarea></td>
                        <td><textarea className="textarea-basic"></textarea></td>
                    </tr>
                </table>

                <h2 className="header-outside">Self-Evaluation (Outside School)</h2>

                {/*<div className="goals-banner-outside">Goals!</div>*/}

                <table className="goal-status-table">
                    <tr>
                        <th className="table-header-outside">My goal outside of school last year was...</th>
                        <th className="table-header-outside">What's the status of the goal?</th>
                    </tr>
                    <tr>
                        <td>
                            <textarea className="textarea-small"></textarea>
                        </td>
                        <td>
                            <input type="checkbox"/> Accomplished<br/>
                            <input type="checkbox"/> Still working
                        </td>
                    </tr>
                </table>

                <label>ONE specific goal I would like to achieve outside of school by the end of this year:</label>
                <textarea className="textarea-small-margin"></textarea>

                <br/><br/>
                <label>How will I know I have achieved this goal?</label>
                <textarea className="textarea-small-margin"></textarea>

                <br/><br/>
                <label>To achieve this goal, things I need to do:</label>
                <table className="action-table-outside">
                    <tr>
                        <th>A week from now:</th>
                        <th>6 weeks from now:</th>
                        <th>6 months from now:</th>
                    </tr>
                    <tr>
                        <td>
                            <textarea className="textarea-medium"></textarea>
                        </td>
                        <td>
                            <textarea className="textarea-medium"></textarea>
                        </td>
                        <td>
                            <textarea className="textarea-medium"></textarea>
                        </td>
                    </tr>
                </table>

                <label>Things that will help me achieve this goal:</label>
                <table className="support-table-outside">
                    <tr>
                        <th>My strengths/abilities</th>
                        <th>Support I get at home/in the community</th>
                        <th>Support I get at school</th>
                    </tr>
                    <tr>
                        <td>
                            <textarea className="textarea-medium"></textarea>
                        </td>
                        <td>
                            <textarea className="textarea-medium"></textarea>
                        </td>
                        <td>
                            <textarea className="textarea-medium"></textarea>
                        </td>
                    </tr>
                </table>

                <label>Why does this goal matter to you?</label>
                <textarea className="textarea-extra"></textarea>

                <br/><br/>
                <div className="feedback-div">
                    <label>Some goals I have achieved till date and am proud of/happy with...</label>
                    <textarea className="textarea-extra"></textarea>
                </div>
            </div>

            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{ marginBottom: 16 }}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* 1. Self-Evaluation *!/*/}
            {/*        <Title level={4}>1. Self-Evaluation</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How would you describe your performance in school last year in the following areas?"*/}
            {/*            name="self_evaluation_performance"*/}
            {/*            rules={[{ required: true, message: 'Please describe your performance.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={4} placeholder="Enter your response" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What specific achievements are you proud of from last year?"*/}
            {/*            name="self_evaluation_achievements"*/}
            {/*            rules={[{ required: true, message: 'Please list your achievements.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your achievements" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What challenges did you face, and how did you overcome them?"*/}
            {/*            name="self_evaluation_challenges"*/}
            {/*            rules={[{ required: true, message: 'Please describe the challenges you faced.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your challenges and solutions" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What are your strongest skills or abilities that helped you last year?"*/}
            {/*            name="self_evaluation_strengths"*/}
            {/*            rules={[{ required: true, message: 'Please list your strengths.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your strengths" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What habits or areas do you think need improvement?"*/}
            {/*            name="self_evaluation_improvements"*/}
            {/*            rules={[{ required: true, message: 'Please identify areas for improvement.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter areas for improvement" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What motivates you to do your best in school or in life in general?"*/}
            {/*            name="self_evaluation_motivation"*/}
            {/*            rules={[{ required: true, message: 'Please describe your motivations.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your motivations" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How do you typically handle setbacks or failures?"*/}
            {/*            name="self_evaluation_setbacks"*/}
            {/*            rules={[{ required: true, message: 'Please describe how you handle setbacks.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your handling strategies" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* 2. Career Aspirations *!/*/}
            {/*        <Title level={4}>2. Career Aspirations</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What are your top 3–4 career aspirations?"*/}
            {/*            name="career_aspirations"*/}
            {/*            rules={[{ required: true, message: 'Please list your career aspirations.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter your career aspirations" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Why do these career aspirations matter to you?"*/}
            {/*            name="career_aspirations_reason"*/}
            {/*            rules={[{ required: true, message: 'Please explain why these aspirations matter.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your reasons" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What skills or qualifications do you think are needed to achieve these career aspirations?"*/}
            {/*            name="career_aspirations_skills"*/}
            {/*            rules={[{ required: true, message: 'Please list necessary skills and qualifications.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter required skills and qualifications" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What steps can you take now to prepare for these aspirations?"*/}
            {/*            name="career_aspirations_steps"*/}
            {/*            rules={[{ required: true, message: 'Please outline the steps you can take.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter your action steps" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will achieving these aspirations impact your life and the lives of others?"*/}
            {/*            name="career_aspirations_impact"*/}
            {/*            rules={[{ required: true, message: 'Please describe the impact.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the impact of achieving your aspirations" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* 3. School-Related Goals *!/*/}
            {/*        <Title level={4}>3. School-Related Goals</Title>*/}

            {/*        <Title level={5}>Past Goals</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What was your main goal in school last year?"*/}
            {/*            name="school_related_past_goal"*/}
            {/*            rules={[{ required: true, message: 'Please describe your past goal.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter your past goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Why was this goal important to you?"*/}
            {/*            name="school_related_past_goal_importance"*/}
            {/*            rules={[{ required: true, message: 'Please explain why this goal was important.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the importance of your past goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did you accomplish this goal? If not, what challenges did you face?"*/}
            {/*            name="school_related_past_goal_accomplishment"*/}
            {/*            rules={[{ required: true, message: 'Please describe the outcome of your past goal.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the outcome of your past goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Title level={5}>New Goals</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What is one specific goal you want to achieve in school by the end of this year?"*/}
            {/*            name="school_related_new_goal"*/}
            {/*            rules={[{ required: true, message: 'Please describe your new goal.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter your new goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Why is this goal important to you?"*/}
            {/*            name="school_related_new_goal_importance"*/}
            {/*            rules={[{ required: true, message: 'Please explain why this goal is important.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the importance of your new goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will achieving this goal help you in the future?"*/}
            {/*            name="school_related_new_goal_future_help"*/}
            {/*            rules={[{ required: true, message: 'Please describe how this goal will help you in the future.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter how this goal will help you in the future" />*/}
            {/*        </Form.Item>*/}

            {/*        <Title level={5}>Action Steps</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What actions can you take a week from now?"*/}
            {/*            name="school_related_action_steps_week"*/}
            {/*            rules={[{ required: true, message: 'Please describe actions for a week from now.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter actions for a week from now" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What actions can you take 6 weeks from now?"*/}
            {/*            name="school_related_action_steps_six_weeks"*/}
            {/*            rules={[{ required: true, message: 'Please describe actions for 6 weeks from now.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter actions for 6 weeks from now" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What actions can you take 6 months from now?"*/}
            {/*            name="school_related_action_steps_six_months"*/}
            {/*            rules={[{ required: true, message: 'Please describe actions for 6 months from now.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter actions for 6 months from now" />*/}
            {/*        </Form.Item>*/}

            {/*        <Title level={5}>Progress Tracking</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How will you measure your progress toward this goal?"*/}
            {/*            name="school_related_progress_tracking"*/}
            {/*            rules={[{ required: true, message: 'Please describe how you will measure progress.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter how you will measure progress" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What will indicate that you have achieved this goal?"*/}
            {/*            name="school_related_goal_achievement_indicators"*/}
            {/*            rules={[{ required: true, message: 'Please describe indicators of goal achievement.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter indicators of goal achievement" />*/}
            {/*        </Form.Item>*/}

            {/*        <Title level={5}>Support System</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What strengths or abilities will help you achieve this goal?"*/}
            {/*            name="school_related_support_strengths"*/}
            {/*            rules={[{ required: true, message: 'Please describe your strengths or abilities.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter your strengths or abilities" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What support can you get at Home or in the community?"*/}
            {/*            name="school_related_support_home"*/}
            {/*            rules={[{ required: true, message: 'Please describe support at home or in the community.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter support available at home or in the community" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What support can you get at School?"*/}
            {/*            name="school_related_support_school"*/}
            {/*            rules={[{ required: true, message: 'Please describe support available at school.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter support available at school" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* 4. Goals Outside of School *!/*/}
            {/*        <Title level={4}>4. Goals Outside of School</Title>*/}

            {/*        <Title level={5}>Past Goals</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What was your main goal outside of school last year?"*/}
            {/*            name="goals_outside_past_goal"*/}
            {/*            rules={[{ required: true, message: 'Please describe your past outside goal.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter your past outside goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Why was this goal important to you?"*/}
            {/*            name="goals_outside_past_goal_importance"*/}
            {/*            rules={[{ required: true, message: 'Please explain why this goal was important.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the importance of your past outside goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did you accomplish this goal? If not, what challenges did you face?"*/}
            {/*            name="goals_outside_past_goal_accomplishment"*/}
            {/*            rules={[{ required: true, message: 'Please describe the outcome of your past outside goal.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the outcome of your past outside goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Title level={5}>New Goals</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What is one specific goal you want to achieve outside of school by the end of this year?"*/}
            {/*            name="goals_outside_new_goal"*/}
            {/*            rules={[{ required: true, message: 'Please describe your new outside goal.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter your new outside goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Why is this goal important to you?"*/}
            {/*            name="goals_outside_new_goal_importance"*/}
            {/*            rules={[{ required: true, message: 'Please explain why this goal is important.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the importance of your new outside goal" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will achieving this goal impact your personal growth?"*/}
            {/*            name="goals_outside_new_goal_impact"*/}
            {/*            rules={[{ required: true, message: 'Please describe the impact of this goal on your personal growth.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the impact of achieving this goal on your personal growth" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* 5. Motivation and Reflection *!/*/}
            {/*        <Title level={4}>5. Motivation and Reflection</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Why does this goal matter to you personally?"*/}
            {/*            name="motivation_reflection_personal_matter"*/}
            {/*            rules={[{ required: true, message: 'Please describe why this goal matters to you personally.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter why this goal matters to you personally" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will achieving this goal make you feel?"*/}
            {/*            name="motivation_reflection_achievement_feeling"*/}
            {/*            rules={[{ required: true, message: 'Please describe how achieving this goal will make you feel.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter how achieving this goal will make you feel" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What are some rewards you will give yourself when you achieve this goal?"*/}
            {/*            name="motivation_reflection_rewards"*/}
            {/*            rules={[{ required: true, message: 'Please describe the rewards you will give yourself.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter rewards you will give yourself upon achievement" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will achieving this goal influence your future plans or aspirations?"*/}
            {/*            name="motivation_reflection_future_influence"*/}
            {/*            rules={[{ required: true, message: 'Please describe how achieving this goal will influence your future.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter how achieving this goal will influence your future plans or aspirations" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* 6. Reflection on Achievements *!/*/}
            {/*        <Title level={4}>6. Reflection on Achievements</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What are some goals you have achieved so far that you are proud of?"*/}
            {/*            name="reflection_achievements_proud_goals"*/}
            {/*            rules={[{ required: true, message: 'Please list some achieved goals you are proud of.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter achieved goals you are proud of" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How did you overcome challenges to achieve these goals?"*/}
            {/*            name="reflection_achievements_overcome_challenges"*/}
            {/*            rules={[{ required: true, message: 'Please describe how you overcame challenges.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter how you overcame challenges to achieve these goals" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What lessons have you learned from setting and working toward your goals?"*/}
            {/*            name="reflection_achievements_lessons_learned"*/}
            {/*            rules={[{ required: true, message: 'Please describe the lessons you have learned.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Enter the lessons you have learned from working toward your goals" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* 7. Personal Development *!/*/}
            {/*        <Title level={4}>7. Personal Development</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What new skills or habits would you like to develop this year?"*/}
            {/*            name="personal_development_new_skills"*/}
            {/*            rules={[{ required: true, message: 'Please describe new skills or habits you want to develop.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter new skills or habits you want to develop" />*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will these skills or habits contribute to achieving your goals?"*/}
            {/*            name="personal_development_skills_contribution"*/}
            {/*            rules={[{ required: true, message: 'Please describe how these skills will contribute to your goals.' }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Enter how these skills or habits will help you achieve your goals" />*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );
};

export default GoalsSettingPartA;
